import type { Identifier } from "ra-core";

import {
  buildInboxRows,
  buildLeadSignals,
  hasUnread,
} from "../../whatsapp/whatsappInboxRules";
import type {
  WhatsAppClockAlertRow,
  WhatsAppConversationFlagsRow,
  WhatsAppCounterRow,
  WhatsAppInboxMessageRow,
  WhatsAppInboxPage,
  WhatsAppInboxParams,
  WhatsAppLeadSignal,
} from "../../types";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic EW2 (TASK-080): Supabase-side inbox, lead-badge, link and counter methods. Every
 * read goes through the RLS-bound tables / views of TASK-076, so an asesora only ever gets the
 * conversations of leads she can read and the counter function itself rejects anyone but
 * Direccion. Rows returned to the UI never carry a phone number or a number id.
 */
const CONVERSATION_COLUMNS =
  "id, deal_id, last_message_at, last_inbound_at, last_outbound_at";
export type ConversationBase = Omit<
  WhatsAppConversationFlagsRow,
  "last_read_at"
>;
/** Recent messages fetched per inbox page row (preview + unread count); bounded on purpose. */
const MESSAGES_PER_ROW = 20;
const SIGNAL_CONVERSATIONS_LIMIT = 1000;
const SIGNAL_MESSAGES_LIMIT = 3000;

export const fail = (error: { message: string } | null, fallback: string) => {
  if (error) throw new Error(error.message || fallback);
};

/**
 * Adds the caller's own read mark to each conversation. RLS on `whatsapp_conversation_reads`
 * returns only her rows, so a missing row simply means "never read by her".
 */
export const withOwnReadMarks = async <T extends ConversationBase>(
  conversations: T[],
): Promise<(T & Pick<WhatsAppConversationFlagsRow, "last_read_at">)[]> => {
  if (conversations.length === 0) return [];
  const { data, error } = await getSupabaseClient()
    .from("whatsapp_conversation_reads")
    .select("conversation_id, last_read_at")
    .in(
      "conversation_id",
      conversations.map((row) => row.id),
    );
  fail(error, "No se pudieron leer las marcas de lectura de WhatsApp");
  const marks = new Map(
    (
      (data ?? []) as { conversation_id: Identifier; last_read_at: string }[]
    ).map((row) => [String(row.conversation_id), row.last_read_at]),
  );
  return conversations.map((row) => ({
    ...row,
    last_read_at: marks.get(String(row.id)) ?? null,
  }));
};

export const createSupabaseWhatsAppInboxMethods = () => ({
  /** Marks a conversation read for the caller only (the function refuses an unreadable one). */
  async markWhatsAppConversationRead(
    conversationId: Identifier,
  ): Promise<void> {
    const { error } = await getSupabaseClient().rpc(
      "mark_whatsapp_conversation_read",
      { p_conversation_id: conversationId },
    );
    fail(error, "No se pudo marcar la conversacion como leida");
  },

  /** One page of conversations (newest first), linked to a lead or, with `unmatched`, without one. */
  async getWhatsAppInbox(
    params: WhatsAppInboxParams,
  ): Promise<WhatsAppInboxPage> {
    const client = getSupabaseClient();
    const from = (params.page - 1) * params.perPage;
    const base = client
      .from("whatsapp_conversations")
      .select(CONVERSATION_COLUMNS, { count: "exact" });
    const { data, error, count } = await (
      params.unmatched
        ? base.is("deal_id", null)
        : base.not("deal_id", "is", null)
    )
      .order("last_message_at", { ascending: false, nullsFirst: false })
      .range(from, from + params.perPage - 1);
    fail(error, "No se pudo leer la bandeja de WhatsApp");
    const conversations = await withOwnReadMarks(
      (data ?? []) as ConversationBase[],
    );
    if (conversations.length === 0) return { rows: [], total: count ?? 0 };

    const ids = conversations.map((row) => row.id);
    const dealIds = conversations
      .map((row) => row.deal_id)
      .filter((id): id is Identifier => id != null);
    const [states, messages, deals] = await Promise.all([
      client
        .from("whatsapp_conversation_deadlines")
        .select("conversation_id, can_free_text, ctwa_open")
        .in("conversation_id", ids),
      client
        .from("whatsapp_messages")
        .select("conversation_id, direction, type, body, message_at")
        .in("conversation_id", ids)
        .order("message_at", { ascending: false })
        .limit(ids.length * MESSAGES_PER_ROW),
      dealIds.length > 0
        ? client.from("deals").select("id, name").in("id", dealIds)
        : Promise.resolve({ data: [], error: null }),
    ]);
    fail(states.error, "No se pudo leer el estado de las ventanas");
    fail(messages.error, "No se pudieron leer los mensajes de WhatsApp");
    fail(deals.error, "No se pudieron leer los leads");

    const windowStates = new Map(
      (
        (states.data ?? []) as {
          conversation_id: Identifier;
          can_free_text: boolean;
          ctwa_open: boolean;
        }[]
      ).map((row) => [String(row.conversation_id), row]),
    );
    const leadNames = new Map(
      ((deals.data ?? []) as { id: Identifier; name: string }[]).map((deal) => [
        String(deal.id),
        deal.name,
      ]),
    );
    return {
      rows: buildInboxRows(
        conversations,
        windowStates,
        (messages.data ?? []) as WhatsAppInboxMessageRow[],
        leadNames,
      ),
      total: count ?? conversations.length,
    };
  },

  /**
   * The lead-row signals of every lead the caller can read: the 'responde' flag, unread counts
   * and the two clock alert flags (`whatsapp_lead_clock_alerts`, thresholds are config data).
   */
  async getWhatsAppLeadSignals(): Promise<WhatsAppLeadSignal[]> {
    const client = getSupabaseClient();
    const [conversations, alerts] = await Promise.all([
      client
        .from("whatsapp_conversations")
        .select(CONVERSATION_COLUMNS)
        .not("deal_id", "is", null)
        .not("last_inbound_at", "is", null)
        .order("last_inbound_at", { ascending: false })
        .limit(SIGNAL_CONVERSATIONS_LIMIT),
      client
        .from("whatsapp_lead_clock_alerts")
        .select("deal_id, alert_24h, alert_ctwa"),
    ]);
    fail(conversations.error, "No se pudo leer la bandeja de WhatsApp");
    fail(alerts.error, "No se pudieron leer las alertas de WhatsApp");
    const rows = await withOwnReadMarks(
      (conversations.data ?? []) as ConversationBase[],
    );
    const unreadIds = rows.filter(hasUnread).map((row) => row.id);
    let inbound: WhatsAppInboxMessageRow[] = [];
    if (unreadIds.length > 0) {
      const { data, error } = await client
        .from("whatsapp_messages")
        .select("conversation_id, direction, type, body, message_at")
        .in("conversation_id", unreadIds)
        .eq("direction", "in")
        .order("message_at", { ascending: false })
        .limit(SIGNAL_MESSAGES_LIMIT);
      fail(error, "No se pudieron leer los mensajes de WhatsApp");
      inbound = (data ?? []) as WhatsAppInboxMessageRow[];
    }
    return buildLeadSignals(
      rows,
      inbound,
      (alerts.data ?? []) as WhatsAppClockAlertRow[],
    );
  },

  /** Links an unmatched conversation to a lead (direccion / lead_manager, enforced by the function). */
  async linkWhatsAppConversation(
    conversationId: Identifier,
    dealId: Identifier,
  ): Promise<void> {
    const { error } = await getSupabaseClient().rpc(
      "link_whatsapp_conversation",
      { p_conversation_id: conversationId, p_deal_id: dealId },
    );
    fail(error, "No se pudo vincular la conversacion");
  },

  /**
   * First step of 'Crear lead': a contact carrying the conversation's number (read here, never
   * shown). The lead itself is then created through the regular lead form.
   */
  async createWhatsAppConversationContact(
    conversationId: Identifier,
  ): Promise<Identifier> {
    // The function owns the contact to the caller and satisfies the contacts guard.
    const { data, error } = await getSupabaseClient().rpc(
      "create_whatsapp_conversation_contact",
      { p_conversation_id: conversationId },
    );
    fail(error, "No se pudo crear el contacto");
    return data as Identifier;
  },

  /**
   * The 'Mensajes de atencion del mes' counter of the month `anchor` falls in (Mexico City
   * calendar month, decided by the database). Direccion only: the function raises otherwise.
   * One aggregate call; the number ids are dropped here.
   */
  async getWhatsAppCounter(anchor: string): Promise<WhatsAppCounterRow[]> {
    const { data, error } = await getSupabaseClient().rpc(
      "whatsapp_monthly_counter",
      { p_now: anchor },
    );
    fail(error, "No se pudo leer el contador de WhatsApp");
    return (
      (data ?? []) as {
        month_start: string;
        month_end: string;
        service_messages: number;
        monthly_free_quota: number;
        paid_templates: number;
      }[]
    ).map((row) => ({
      monthStart: row.month_start,
      monthEnd: row.month_end,
      serviceMessages: row.service_messages,
      monthlyFreeQuota: row.monthly_free_quota,
      paidTemplates: row.paid_templates,
    }));
  },
});
